<template>
    <div class="px-2 py-1 my-1 w-full">
        <select-series-modal v-model:show-modal="selectingSeries"
                             :points="seriesList"
                             :scaling-profiles="scalingProfiles"
                             v-model:error-multiple-scaling-profiles="errors['errorAddingSeriesTooManyScalingProfiles']"
                             v-model:series-visible="seriesToShow"
        ></select-series-modal>

        <div v-if="loadingPageStatus === loadingStatus.meta">
            <div class="flex flex-row items-center justify-center py-4">
                <span :class="iconLoading"></span>
                <span class="text-grey-500 text-base font-medium">Loading Point Information, please wait.</span>
            </div>
        </div>

        <div v-else-if="loadingPageStatus === loadingStatus.data"
             class="flex flex-row items-center justify-center py-4">
            <span :class="iconLoading"></span>
            <span class="text-grey-500 text-base font-medium">Loading Data on Graph.</span>
        </div>

        <div v-if="loadingPageStatus === loadingStatus.complete && numData() === 0"
             class="text-md">
            <p>
                No data found in specified range.
            </p>
            <p>
                Last Check-in was {{ prettyLastUpdateTime }}.
            </p>
        </div>

        <div class="w-full flex">
            <div class="w-1/3"></div>
            <div class="w-1/3 justify-self-center">
                <div v-if="errorMessage !== ''">
                    <banner-error>
                        <template #title>Error Exporting Points</template>
                        <template #content>{{ errorMessage }}</template>
                    </banner-error>
                </div>
            </div>

            <div class="w-1/3 justify-self-end flex flex-row">
                <div class="mb-1 px-2 w-full flex justify-end">
                    <div v-if="!isHeavyLoad() && showBackFillToggle()">
                        <tooltip-container :width="300">
                            <template #tooltip-text>
                                Enabling this feature highlights readings in which Sonicu's DataSync technology
                                has ensured all of your data made it to SoniCloud, protecting you against disruptions
                                in connectivity.
                            </template>

                            <div class="flex flex-col items-center mr-4 rounded-md border p-3 py-2">
                                <span>Highlight Data Sync</span>
                                <div>
                                    <toggle id="show_back_fill_windows"
                                            v-model:value="loadBackFillWindows"
                                            :togglable="true"
                                            name="show_back_fill_windows"
                                    ></toggle>
                                </div>
                            </div>
                        </tooltip-container>
                    </div>

                    <div v-if="this.numData() > 0 && showQaTestData()"
                         class="flex flex-col items-center mr-4 rounded-md border p-3 py-2">
                        <span>Number of Points</span>
                        <span>{{ numData() }}</span>
                    </div>

                    <div v-if="this.numData() > 0 && showQaTestData()"
                         class="py-2 px-4 border-solid border-gray-900 bg-blue-500 text-white rounded-md flex items-center">
                        <div class="flex items-center">
                            <small-colored-check
                                v-model="useUTC"
                                :togglable="true"
                            ></small-colored-check>
                            <span class="lnr icon-earth"></span>
                        </div>
                        <span class="ml-2">{{ timezone }}</span>
                    </div>
                </div>
            </div>
        </div>

        <div
            class="relative border-gray-300 rounded-md"
            :class="[pointsLoaded() ? 'border border-solid' : '']"
            :style="graphHeight">
            <div v-show="numData() === 0"
                 class="absolute bg-white w-full"
                 :class="[loadingPageStatus === loadingStatus.complete && numData() === 0 ? 'z-50' : '']"
                 :style="graphHeight"></div>
            <div class="absolute w-full"
                 id="meter-graph-am5"
                 ref="meterGraph"
                 :style="graphHeight"
            ></div>
        </div>
    </div>
</template>

<script>
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import {iconNamesMixin} from "@/mixins/IconNamesMixin"

import {Am5Settings} from "@/mixins/am5charts/Am5Settings";
import {Am5SeriesColor} from "@/mixins/am5charts/Am5SeriesColor";

import axios from "axios"
import _ from "lodash"

import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import {Exporting} from "@amcharts/amcharts5/plugins/exporting";
import {ExportingMenu} from "@amcharts/amcharts5/plugins/exporting";
import {Annotator} from "@amcharts/amcharts5/plugins/exporting";
import {addLicense as addAM5License} from "@amcharts/amcharts5";
import {DateTime as LuxonDatetime} from 'luxon';

import SmallColoredCheck from "@/components/elements/SmallColoredCheck";
import {Am5Series} from "@/mixins/am5charts/Am5Series"
import {Am5SideAxis} from "@/mixins/am5charts/Am5SideAxis"
import {Am5DateAxis} from "@/mixins/am5charts/Am5DateAxis"
import SelectSeriesModal from "@/components/SelectSeriesModal";
import BannerError from "./Banners/BannerError.vue";
import TooltipContainer from "./elements/TooltipContainer.vue";
import Toggle from "./elements/toggle.vue";

const LOADING_POINT_META = 0
const LOADING_POINT_DATA = 1
const LOADING_POINT_DATA_COMPLETE = 3

const MAX_POINTS_PER_REQUEST = 500;

export default {
    name: "MeterGraphAm5",
    mixins: [SonicuCoreMixin, iconNamesMixin],
    components: {
        Toggle,
        TooltipContainer, BannerError, SelectSeriesModal, SmallColoredCheck,
    },
    props: {
        id: Number,
        name: String,
        useShortGaps: Boolean,
        startDate: {
            type: Date,
            default() {
                const yesterday = new Date();
                yesterday.setDate(yesterday.getDate() - 1);
                return yesterday;
            },
        },
        endDate: {
            type: Date,
            default() {
                return new Date()
            }
        },
        fileName: String,
        timezone: String,
        filterDataReady: {
            type: Boolean,
            default: true,
        },
        pairPointOptions: {},
        showPairedPoint: Boolean,
        pairedPoints: {
            type: [Array, Object],
            default() {
                return {}
            }
        },
        pointIds: Array,
        showSeriesModal: Boolean,
        username: String,
        validationReadings: {
            type: Array,
            default: null,
        },
    },
    data() {
        return {
            chartReady: false,
            useUTC: false,
            selectingSeries: false,
            loadBackFillWindows: false,

            loadFunctionTmp: null,
            updateGraphDebounce: null,

            scalingProfiles: [],
            seriesToShow: [`${this.id}`],

            lastUpdateTime: null,
            requestNumber: 0,
            seriesSummaryPageNumber: 0,

            companyTimezone: null,

            loadingStatus: {
                meta: 0,
                data: 1,
                complete: 2,
            },
            numPointPages: 0,
            pointPagesLoaded: 0,
            loadingPageStatus: 0,
            isLoadScalingProfileComplete: false,
            isLoadSeriesSummaryDataComplete: false,

            errors: {
                errorAddingSeriesTooManyScalingProfiles: false,
            },

            renderedScalingProfiles: [],
            chartTitle: '',
            errorMessage: '',
        }
    },
    created() {
        // Non-reactive properties (not in data()).
        //
        // Anything that owns — or can reach — an am5 object MUST live here rather
        // than in data(). Vue's reactive() proxies objects deeply, so an am5
        // Sprite reached through data() is a *different object identity* than the
        // same Sprite reached directly. am5 removes a child's canvas display with
        // `$array.removeFirst(container._children, child._display)`, which matches
        // by ===; when the add went through one identity and the remove through
        // the other, the lookup misses, the display is never spliced out, and the
        // orphan keeps painting itself at its stale geometry. That is what surfaces
        // as duplicated, offset "ghost" copies of a series that don't move with the
        // scrollbar, accumulating with every series toggle.
        this.chart = null
        this.root = null
        this.seriesList = {}
        this.leftAxis = null
        this.dateAxis = null
        this.exporting = null
        this.annotator = null
        this.yAxis = []
        this.legend = null
        this._scrollbarIsHeavy = false

        Am5SeriesColor.resetColors()

        // Add AM5 license
        addAM5License("AM5C-394244706")

        this.useUTC = false
        Am5Settings.updateVueHook = _.debounce(event => {
            let loadingComplete = false
            if (this.loadingPageStatus === this.loadingStatus.data && !this.isLoading()) {
                this.loadingPageStatus = this.loadingStatus.complete
                loadingComplete = true
            }

            // Only update axis intervals and scrollbar when all data has loaded,
            // not during batch loading. This avoids redundant recalculations
            // (the am4 equivalent had a cascading regrouping bug).
            if (loadingComplete || this.loadingPageStatus === this.loadingStatus.complete) {
                this.dateAxis.updateDateAxisInterval()

                // The scrollbar and legend are built before any data has arrived,
                // so the point-count half of isHeavyLoad() could not have been
                // evaluated yet. Re-decide now that the data has landed, but only
                // when the verdict actually changed -- rebuilding the scrollbar on
                // every batch would be pure churn.
                if (this._scrollbarIsHeavy !== this.isHeavyLoad()) {
                    this.setupScrollbar()
                    this.setupLegend()
                }

                this.syncScrollbarData()
            }

            // After data loads, ensure the chart shows the full requested date range.
            // Defer to next frame so am5 processes axis min/max changes first.
            if (this._pendingDateRangeZoom && this.dateAxis && this.dateAxis.axisInitialized() && !this.isLoading()) {
                this._pendingDateRangeZoom = false
                setTimeout(() => {
                    if (this.dateAxis && this.dateAxis.axisInitialized()) {
                        this.dateAxis.axis.zoomToDates(this.dateAxis.start, this.dateAxis.end, 0)
                    }
                    let scrollbarX = this.chart ? this.chart.get("scrollbarX") : null
                    if (scrollbarX) {
                        scrollbarX.set("start", 0)
                        scrollbarX.set("end", 1)
                    }
                }, 50)
            }

            this.$forceUpdate()
        }, 100)

        this.loadScalingProfileData()

        this.requestNumber++
        this.selectingSeries = false
    },
    mounted() {
        Am5Settings.useShortGaps = this.useShortGaps

        // Create root
        this.root = am5.Root.new(this.$refs.meterGraph)
        this.root.setThemes([am5themes_Animated.new(this.root)])
        this.root.timezone = am5.Timezone.new(this.timezone)

        // Create chart
        // maxTooltipDistance: 0 with maxTooltipDistanceBy: "x" shows tooltips for
        // all series at the same X position (all series share the same date axis,
        // so X distance between tooltips is always 0). Using -1 would only show
        // the single closest tooltip.
        this.chart = this.root.container.children.push(
            am5xy.XYChart.new(this.root, {
                panX: false,
                panY: false,
                wheelX: "panX",
                wheelY: "zoomX",
                layout: this.root.verticalLayout,
                maxTooltipDistance: 0,
                maxTooltipDistanceBy: "x",
            })
        )
        this.chart.set("visible", false)

        // Cursor
        // snapToSeriesBy: "x" snaps the cursor to the nearest data point by
        // X position (timestamp) rather than XY distance, so hovering shows
        // values at the closest timestamp regardless of Y position.
        this.cursor = am5xy.XYCursor.new(this.root, {
            behavior: "zoomX",
            snapToSeriesBy: "x",
        })
        this.chart.set("cursor", this.cursor)

        // Build axes
        this.leftAxis = new Am5SideAxis(this.root)
        this.yAxis = [this.leftAxis]
        this.dateAxis = new Am5DateAxis(this.root, this.startDate, this.endDate, this.timezone)

        this.leftAxis.buildAndRender(this.chart)
        this.leftAxis.renderLeft()
        this.dateAxis.buildAndRender(this.chart)


        this.loadSeriesSummaryData()

        if (this.permissions.isSonicu() || (this.environment.isQa() || this.environment.isLocal() || this.environment.isStaging())) {
            this.loadBackFillWindows = true
        }
    },
    methods: {
        loadScalingProfileData() {
            console.time('MeterGraphAm5@loadScalingProfileData')
            axios.get(this.company.buildCompanyApiInternalRoute('current-profile'), {
                params: {}
            }).then(response => {
                this.scalingProfiles = response.data
                console.timeEnd('MeterGraphAm5@loadScalingProfileData')
            }).then(() => {
                this.isLoadScalingProfileComplete = true
                if (this.isLoadSeriesSummaryDataComplete) {
                    this.chart.set("visible", true)
                }
            })
        },
        loadSeriesSummaryData() {
            this.loadingPageStatus = this.loadingStatus.meta

            let numPages = _.size(this.pointIds) / MAX_POINTS_PER_REQUEST
            this.numPointPages = _.ceil(numPages)
            this.pointPagesLoaded = 0

            _.each(_.range(this.numPointPages), pageNumber => {
                this.loadNextSeriesSummaryDataPage(pageNumber)
            })
        },
        loadNextSeriesSummaryDataPage(pageNumber) {
            axios.get(this.company.buildCompanyApiInternalRoute('graph-meter') + '/' + this.id, {
                params: {
                    requestNumber: this.requestNumber,
                    page: pageNumber,
                }
            }).then(response => {
                if (parseInt(response.data.requestNumber) !== this.requestNumber) {
                    return
                }
                let data = response.data.pointData
                let mainSeriesFound = false
                _.each(data, (point, id) => {
                    let seriesData = {
                        id: id,
                        name: point.name,
                        serialNumber: point.serialNumber,
                        requestNumber: point.requestNumber,
                        alarmProfileId: point.alarmProfileId,
                        scalingProfileId: point.scalingProfileId,
                        alarmInfo: point.alarmInfo,
                        lastUpdateTime: point.lastUpdateTime
                    }
                    if (!_.has(this.seriesList, id)) {
                        let tmpScalingProfile = null;
                        if (_.has(this.scalingProfiles, point.scalingProfileId)) {
                            tmpScalingProfile = this.scalingProfiles[point.scalingProfileId]
                        }
                        this.seriesList[id] = new Am5Series(this.root, this.company.getCompanyId(), this.chart, id, this.leftAxis, tmpScalingProfile, this.startDate, this.endDate, Am5Settings.AXIS_LEFT, this.loadBackFillWindows)
                    }
                    let s = this.seriesList[id]
                    s.updateFromSeriesData(seriesData)
                    s.setValidationReadings(this.validationReadings)
                    s.setDateAxis(this.dateAxis)

                    if (id === `${this.id}` && this.leftAxis.series.length === 0) {
                        this.leftAxis.addSeries(s)
                        if (_.has(this.scalingProfiles, s.scalingProfileId)) {
                            this.leftAxis.setCurrentProfile(this.scalingProfiles[s.scalingProfileId])
                            this.renderedScalingProfiles.push(this.scalingProfiles[s.scalingProfileId])
                            mainSeriesFound = true
                        }
                        this.lastUpdateTime = point.lastUpdateTime
                    }
                })
                if (_.isNil(response.data.nextPage)) {
                    this.seriesSummaryPageNumber = 0
                }

                if (mainSeriesFound) {
                    this.updateChart()
                }
            })
        },
        updateChart() {
            if (!this.filterDataReady) {
                return false
            }

            if (!_.isNil(this.updateGraphDebounce)) {
                let updateGraphCall = new Promise(() => this.updateGraphDebounce())
                return true
            } else {
                this.updateGraphDebounce = _.debounce(() => {
                    console.log(`Updating Graph (AM5)`)
                    if (!this.chartReady) {
                        if (Object.keys(this.seriesList).indexOf(`${this.id}`) !== -1) {
                            this.setupChart()
                        } else {
                            return
                        }
                    }

                    this.dateAxis.setSeries(this.seriesList)
                    this.dateAxis.updateTimeIntervals(this.startDate, this.endDate)
                    this.root.timezone = am5.Timezone.new(this.timezone)

                    if (this.legend) {
                        this.legend.dispose()
                    }

                    this.setupLegend()
                    this.setupScrollbar()

                    // Flag that scrollbar/axis should reset to full range after data loads
                    this._pendingScrollbarReset = true
                    this._pendingDateRangeZoom = true

                    _.each(this.yAxis, axis => {
                        axis.updateMinMax()
                    })

                    this.dateAxis.updateGapAndMinBulletDistance()
                }, 500)

                return this.updateChart()
            }
        },
        setupChart() {
            this.chartReady = false
            this.legend = null

            this.setupChartConfiguration()
            this.setupChartPlugins()
            this.setupChartTitle()

            this.chartReady = true
        },
        setupChartConfiguration() {
            this.chart.set("visible", true)
        },
        setupChartPlugins() {
            // Export plugin
            this.exporting = Exporting.new(this.root, {
                menu: ExportingMenu.new(this.root, {}),
                filePrefix: this.fileName,
                dateFormat: 'i',
            })

            // Add custom export data menu item
            if (this.permissions.can('can_export_point_data')) {
                this.exporting.get("menu").set("items", [
                    {
                        type: "format",
                        format: "png",
                        label: "Export PNG",
                    },
                    {
                        type: "format",
                        format: "jpg",
                        label: "Export JPG",
                    },
                    {
                        type: "format",
                        format: "csv",
                        label: "Export CSV",
                    },
                    {
                        type: "format",
                        format: "xlsx",
                        label: "Export XLSX",
                    },
                    {
                        type: "custom",
                        label: "Export Data",
                        callback: () => {
                            this.exportData()
                        }
                    },
                    {
                        type: "separator",
                    },
                    {
                        type: "format",
                        format: "print",
                        label: "Print",
                    },
                ])
            }

            this.exporting.events.on("exportstarted", () => {
                if (window.fathomLoaded) {
                    window.fathom.trackGoal('K8TLPFJF', 0)
                }
            })

            // Annotator plugin
            this.annotator = Annotator.new(this.root, {})
        },
        setupChartTitle() {
            this.exporting.events.on("exportstarted", () => {
                this.chartTitle = this._getChartTitleText()
                this._addTitleToChart()
            })
            this.exporting.events.on("exportfinished", () => {
                this.chartTitle = ""
                this._removeTitleFromChart()
            })
        },
        _addTitleToChart() {
            this._titleLabel = this.chart.children.unshift(
                am5.Label.new(this.root, {
                    text: this.chartTitle,
                    fontSize: 20,
                    x: am5.p50,
                    centerX: am5.p50,
                    paddingBottom: 15,
                })
            )
        },
        _removeTitleFromChart() {
            if (this._titleLabel) {
                this._titleLabel.dispose()
                this._titleLabel = null
            }
        },
        _getChartTitleText() {
            let startDate = LuxonDatetime.fromJSDate(this.startDate, {zone: this.companyTimezone})
            let endDate = LuxonDatetime.fromJSDate(this.endDate, {zone: this.companyTimezone})

            let title = `${this.name}; starting at ${startDate.toFormat('MM-dd-yyyy HH:mm:ss')}`
            title += ` and ending at ${endDate.toFormat('MM-dd-yyyy HH:mm:ss')};`
            title += `\nExported by ${this.username} on ${LuxonDatetime.local().toFormat('MM-dd-yyyy HH:mm:ss')}`
            return title
        },
        setupScrollbar() {
            let isHeavy = this.isHeavyLoad()
            this._scrollbarIsHeavy = isHeavy

            // Dispose of existing scrollbar if type needs to change
            let existingScrollbar = this.chart.get("scrollbarX")
            if (existingScrollbar) {
                existingScrollbar.dispose()
            }

            this._scrollbarSeries = null

            if (isHeavy) {
                // Basic scrollbar for heavy loads
                this.chart.set("scrollbarX", am5.Scrollbar.new(this.root, {
                    orientation: "horizontal",
                }))
            } else {
                // Preview scrollbar
                let scrollbar = am5xy.XYChartScrollbar.new(this.root, {
                    orientation: "horizontal",
                    height: 60,
                })
                this.chart.set("scrollbarX", scrollbar)

                let sbChart = scrollbar.chart
                if (sbChart) {
                    // Add date axis to scrollbar chart
                    let sbDateAxis = sbChart.xAxes.push(
                        am5xy.DateAxis.new(this.root, {
                            baseInterval: { timeUnit: "minute", count: 1 },
                            renderer: am5xy.AxisRendererX.new(this.root, {
                                opposite: false,
                                strokeOpacity: 0,
                            }),
                        })
                    )

                    // Set timezone on scrollbar axis
                    sbDateAxis.set("tooltip", am5.Tooltip.new(this.root, {}))

                    // Add value axis to scrollbar chart
                    let sbValueAxis = sbChart.yAxes.push(
                        am5xy.ValueAxis.new(this.root, {
                            renderer: am5xy.AxisRendererY.new(this.root, {}),
                        })
                    )

                    // Add series to scrollbar chart
                    let sbSeries = sbChart.series.push(
                        am5xy.LineSeries.new(this.root, {
                            xAxis: sbDateAxis,
                            yAxis: sbValueAxis,
                            valueYField: "value",
                            valueXField: "date",
                            connect: false,
                            stroke: am5.color('#3366cc'),
                            fill: am5.color('#3366cc'),
                        })
                    )

                    sbSeries.strokes.template.setAll({
                        strokeWidth: 2,
                    })

                    sbSeries.fills.template.setAll({
                        visible: true,
                        fillOpacity: 0.2,
                    })

                    this._scrollbarSeries = sbSeries

                    // Copy data from main series to scrollbar series
                    this.syncScrollbarData()

                    // Set the scrollbar axis range to match the current date range
                    if (this.dateAxis) {
                        sbDateAxis.setAll({
                            min: this.dateAxis.start.getTime(),
                            max: this.dateAxis.end.getTime(),
                            strictMinMax: true,
                        })
                    }

                    // Clear the desaturation filter
                    sbChart.plotContainer.set("filters", [])

                    // Render backfill intervals
                    _.forEach(this.seriesList, series => {
                        if (series.visible && this.loadBackFillWindows) {
                            series.renderBackFillIntervals(scrollbar)
                        }
                    })
                }
            }
        },
        syncScrollbarData() {
            if (!this._scrollbarSeries) {
                return
            }

            let mainSeries = this.seriesList[this.id]
            if (mainSeries && mainSeries.isAm5SeriesReady() && mainSeries.am5Series.data.values.length > 0) {
                // Use the timestamp field (already numeric) for the scrollbar's DateAxis
                let scrollData = mainSeries.am5Series.data.values.map(item => ({
                    date: item.timestamp || (item.date instanceof Date ? item.date.getTime() : item.date),
                    value: item.value,
                }))

                // Add boundary points at the requested date range edges so the
                // scrollbar's date axis spans the full user-requested range, not
                // just the data range. Use the first/last data value to avoid
                // visual spikes in the scrollbar preview.
                if (this.dateAxis && scrollData.length > 0) {
                    let rangeStart = this.dateAxis.start.getTime()
                    let rangeEnd = this.dateAxis.end.getTime()
                    let firstDataTime = scrollData[0].date
                    let lastDataTime = scrollData[scrollData.length - 1].date

                    if (rangeStart < firstDataTime) {
                        scrollData.unshift({ date: rangeStart, value: scrollData[0].value })
                    }
                    if (rangeEnd > lastDataTime) {
                        scrollData.push({ date: rangeEnd, value: scrollData[scrollData.length - 1].value })
                    }
                }

                this._scrollbarSeries.data.setAll(scrollData)
            }

            // Reset viewport to show full date range after date range change
            if (this._pendingScrollbarReset) {
                this._pendingScrollbarReset = false
                let scrollbarX = this.chart.get("scrollbarX")
                if (scrollbarX) {
                    scrollbarX.set("start", 0)
                    scrollbarX.set("end", 1)
                }
            }
        },
        setupLegend() {
            if (!_.isNil(this.legend)) {
                this.legend.dispose()
                this.legend = null
            }

            if (!this.isHeavyLoad()) {
                return null
            }

            let legend = this.chart.children.push(
                am5.Legend.new(this.root, {
                    centerX: am5.p50,
                    x: am5.p50,
                    paddingTop: 10,
                    paddingBottom: 10,
                    paddingLeft: 15,
                    paddingRight: 15,
                })
            )

            let seriesArr = []
            _.each(this.yAxis, axis => {
                seriesArr = _.concat(seriesArr, axis.series)
            })

            let legendData = []
            _.each(seriesArr, s => {
                legendData.push({
                    name: s.name,
                    id: s.id,
                    fill: s.color,
                })
            })

            legend.data.setAll(legendData)

            legend.itemContainers.template.events.on("click", ev => {
                let dataContext = ev.target.dataItem.dataContext
                let id = dataContext.id
                let series = this.seriesList[id]
                if (series.visible) {
                    series.hide()
                } else {
                    series.show()
                }
            })

            this.legend = legend
        },
        updateCursorSnapToSeries() {
            if (_.isNil(this.cursor)) {
                return
            }
            // Only put the PRIMARY series in snapToSeries so the cursor snaps to it.
            // Secondary series are intentionally excluded: when a series is NOT in
            // snapToSeries, Axis.updateTooltip() calls showDataItemTooltip() for it
            // directly, which sets tooltip.dataItem and pointTo so it renders normally.
            // Series IN snapToSeries have their tooltips controlled by XYChart's
            // _handleCursorPosition() which shows only the nearest — but since only
            // the primary series is in the list, it always wins.
            // maxTooltipDistance: 0 + maxTooltipDistanceBy: "x" ensures secondary
            // series tooltips aren't filtered out by the distance check.
            let primarySeries = null
            _.forEach(this.seriesList, series => {
                if (series.isAm5SeriesReady() && series.isVisible() && `${series.id}` === `${this.id}`) {
                    primarySeries = series.getAm5Series()
                }
            })
            this.cursor.set("snapToSeries", primarySeries ? [primarySeries] : [])
        },
        addSeriesToChart(point) {
            if (_.isNil(point)) {
                return
            }

            this.errors.errorAddingSeriesTooManyScalingProfiles = false

            if (this.leftAxis.checkScalingProfile(point.scalingProfileId)) {
                this.leftAxis.addSeries(point, true)
            } else {
                let pointAdded = false

                _.forEach(this.renderedScalingProfiles, (profile, index) => {
                    if (profile.id === point.scalingProfileId) {
                        this.yAxis[index].addSeries(point, true)
                        pointAdded = true
                    }
                });

                if (!pointAdded) {
                    let scalingProfileIndex = null

                    _.forEach(this.scalingProfiles, (profile, index) => {
                        if (profile.id === point.scalingProfileId) {
                            this.renderedScalingProfiles.push(profile)
                            let sideAxis = new Am5SideAxis(this.root)
                            sideAxis.setCurrentProfile(profile)
                            sideAxis.buildAndRender(this.chart)
                            sideAxis.renderRight()

                            this.yAxis.push(sideAxis)
                            scalingProfileIndex = index
                        }
                    });

                    if (!_.isNil(scalingProfileIndex)) {
                        let lastAxisLength = this.yAxis.length - 1
                        this.yAxis[lastAxisLength].addSeries(point, true)
                    }
                }
            }

            this.updateCursorSnapToSeries()
            return true
        },
        numData() {
            let count = 0
            _.forEach(this.seriesList, item => {
                if (item.isAm5SeriesReady()) {
                    count += item.am5Series.data.values.length
                }
            })

            return count
        },
        isLoading() {
            let isLoading = false
            _.forEach(this.seriesList, series => {
                if (series.visible && series.isLoadingData()) {
                    isLoading = true
                }
            })

            return isLoading
        },
        exportData() {
            this.errorMessage = ''
            axios.get(this.company.buildCompanyApiInternalRoute('data-meter-export'), {
                responseType: 'blob',
                params: {
                    start: this.startDate,
                    end: this.endDate,
                    useUTC: this.useUTC,
                    ids: this.seriesToShow,
                },
            }).then(response => {
                const url = window.URL.createObjectURL(new Blob([
                    response.data
                ]))
                const link = document.createElement('a')
                link.href = url
                link.setAttribute('download', 'graphExport.csv')
                document.body.appendChild(link)
                link.click()
            }).catch((error, data) => {
                const errorMessage = new Blob([
                    error.response.data
                ], {type: 'application/json'})
                errorMessage.text().then((text) => {
                    this.errorMessage = (JSON.parse(text))['data']
                    setTimeout(() => {
                        this.errorMessage = ''
                    }, 10000)
                })
            })
        },
        pointsLoaded() {
            let pointsLoaded = this.loadingPageStatus === this.loadingStatus.complete
                && this.numData() > 0

            this.$emit('hasSeriesForModal', pointsLoaded)
            return pointsLoaded
        },
        pruneYAxis() {
            _.forEach(this.yAxis, (axis, index) => {
                if (axis.series.length === 0) {
                    _.pull(this.yAxis, axis)
                }
            })
        },
        isHeavyLoad() {
            // numData is a method, not a property. Calling it is the point: the
            // bare `this.numData > 15000` compared a Function to a Number, which
            // is always false, so this branch had never fired and a single
            // very-large series was never treated as heavy. With it working, such
            // a series now correctly drops the preview scrollbar (which would have
            // to render every point a second time) in favour of the plain one.
            if (this.numData() > 15000) {
                return true
            }

            let numSeries = 0
            _.forEach(this.yAxis, axis => {
                numSeries += axis.series.length
            })

            return numSeries > 1
        },
        showQaTestData() {
            return this.environment.isQa() || this.environment.isLocal() || this.environment.isStaging()
        },
        showBackFillToggle() {
            let shouldBackFill = false;
            _.each(this.seriesList, s => {
                if (s.isVisible() && s.hasBackFillIntervals()) {
                    shouldBackFill = true
                }
            })

            return shouldBackFill
        },
    },
    watch: {
        useShortGaps(val) {
            Am5Settings.useShortGaps = val
            _.each(this.seriesList, series => {
                if (series.isVisible()) {
                    series.am5Series.data.clear()
                    series.seriesDataLoader.reset()
                    series.loadData(this.startDate, this.endDate)
                }
            })
            this.updateChart()
        },
        useUTC() {
            if (this.useUTC) {
                this.$emit('update:timezone', 'UTC')
            } else {
                this.$emit('update:timezone', this.companyTimezone)
            }
            this.updateChart()
        },
        showPairedPoint() {
            if (this.showPairedPoint && !_.isNil(this.pairedPoints)) {
                _.each(this.pairedPoints, point => {
                    this.seriesToShow.push(`${point.ID}`)
                })
            } else if (!this.showPairedPoint && !this.showSeriesModal) {
                _.remove(this.seriesToShow, value => {
                    return `${value}` !== `${this.id}`
                })
            }
            this.updateChart()
        },
        validationReadings() {
            _.each(this.seriesList, s => {
                s.setValidationReadings(this.validationReadings)
            })
            this.updateChart()
        },
        timezone() {
            if (this.timezone !== 'UTC') {
                this.companyTimezone = this.timezone
            }
            this.dateAxis.setTimezone(this.timezone)
            this.updateChart()
        },
        filterDataReady(val, old) {
            if (this.filterDataReady && !old) {
                this.updateChart()
            }
        },
        chartReady(val, old) {
            if (old === false) {
                let series = this.seriesList[this.id]
                if (!_.isNil(series)) {
                    series.setupSeries()
                    series.setupThresholds()
                    series.loadData(this.startDate, this.endDate)
                    this.loadingPageStatus = this.loadingStatus.data
                    this.updateCursorSnapToSeries()
                }
            }
        },
        seriesToShow: {
            handler(val, old) {
                // The primary point can never be deselected. Put it back in the list so the
                // series modal's checkbox, the single-series thresholds below, and the CSV
                // export all agree with what stays drawn on the chart. The push re-triggers
                // this deep watcher, which then redraws once with the corrected list.
                if (_.indexOf(this.seriesToShow, `${this.id}`) === -1) {
                    this.seriesToShow.push(`${this.id}`)
                    return
                }

                _.each(this.seriesList, (series, key) => {
                    // series.id is a string (it comes from an object key in the graph-meter
                    // response), while the id prop is a Number — compare as strings so the
                    // never-remove-the-primary-point guard actually fires.
                    if (_.indexOf(this.seriesToShow, series.id) === -1 && `${series.id}` !== `${this.id}`) {
                        _.each(this.yAxis, axis => {
                            axis.removeSeries(series)
                        })
                    } else if (!this.seriesList[key].isVisible()) {
                        this.addSeriesToChart(this.seriesList[key])
                    }
                })

                let leftAxis = this.yAxis[0]
                if (this.seriesToShow.length === 1) {
                    _.each(leftAxis.series, series => {
                        series.setupThresholds()
                    })
                } else {
                    _.each(leftAxis.series, series => {
                        series.clearThresholds()
                    })
                }

                _.each(this.yAxis, axis => {
                    axis.updateMinMax()
                    axis.renderAxis()
                })

                this.updateCursorSnapToSeries()
                this.setupScrollbar()
                this.setupLegend()
            },
            deep: true
        },
        showSeriesModal(val, old) {
            if (val !== old) {
                this.selectingSeries = this.showSeriesModal
            }
        },
        selectingSeries(val, old) {
            if (val !== old) {
                this.$emit('update:showSeriesModal', this.selectingSeries)
            }
        },
        pointIds(val, old) {
            if (val !== old) {
                this.loadSeriesSummaryData()
            }
        },
        loadBackFillWindows(val, old) {
            if (val === old) {
                return
            }

            _.each(this.seriesList, series => {
                series.updateLoadingBackFillIntervals(this.loadBackFillWindows)
            })

            _.each(this.seriesList, series => {
                if (series.visible) {
                    series.renderBackFillIntervals()
                }
            })
        },
    },
    computed: {
        totalNumberOfPoints() {
            return this.numData()
        },
        graphHeight() {
            let clientHeight = document.documentElement.clientHeight;
            clientHeight = Math.max(clientHeight - 400, 450);
            return "height: " + clientHeight.toString() + "px;"
        },
        prettyLastUpdateTime() {
            if (!_.isNil(this.lastUpdateTime)) {
                let time = new Date(this.lastUpdateTime)
                return `On: ${time.toLocaleString("en-US", {timeZone: this.timezone})}`
            } else {
                return 'Never'
            }
        },
        hasPairedPoint() {
            return !_.isNil(this.pairPointOptions) && Object.keys(this.pairPointOptions).length > 0
        },
        pairPointSelected() {
            return this.showPairedPoint && !_.isNil(this.pairedPoints)
        },
    },
    beforeUnmount() {
        if (!_.isNil(this.root)) {
            this.root.dispose()
        }
        this.root = null
        this.chart = null
    }
}
</script>

<style scoped>
    #meter-graph-am5 {
        height: 40em
    }
    .dark-theme {
        background-color: #30303d;
        color: #fff;
    }
</style>
